import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { Button } from '@framework/ui/atoms/Button';
import { Input } from '@framework/ui/atoms/Input';
import type { FolderId, WorkspaceId } from 'balus-common';
import { ViewModelOperation } from '@view-model/domain/view-model';
import { useActionLogSender } from '@framework/action-log';
import { useSetAtom } from 'jotai';
import { openFoldersAtom } from '@user/Sidebar/SidebarContainer/SidebarTree/SidebarTreeStore/sidebarTreeOpenState';
import { setScrolledFolderAtom } from '@user/Sidebar/SidebarContainer/SidebarTree/SidebarTreeStore/sidebarTreeControlState';

type Props = {
  workspaceId: WorkspaceId;
  folderId: FolderId;
  isOpen: boolean;
  onClose(): void;
};

export const FolderCreateModal: React.FC<Props> = ({ workspaceId, folderId, isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const actionLogSender = useActionLogSender();

  const [name, setName] = useState<string>('');
  const [canSave, setCanSave] = useState<boolean>(false);

  useEffect(() => {
    setCanSave(name.trim() !== '');
  }, [name]);

  const setScrolledFolder = useSetAtom(setScrolledFolderAtom);
  const openFolders = useSetAtom(openFoldersAtom);

  const handleSubmit = useCallback(async () => {
    const newFolderId = await ViewModelOperation.createFolder(workspaceId, folderId, name, actionLogSender);
    setScrolledFolder(newFolderId);
    openFolders({ folderIds: [folderId], workspaceId });
    onClose();
    setName('');
  }, [actionLogSender, folderId, name, onClose, openFolders, setScrolledFolder, workspaceId]);

  const handleClose = useCallback(() => {
    setName('');
    onClose();
  }, [onClose]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('workspace.folderCreate.title')}
      submitButton={
        <Button onClick={handleSubmit} disabled={!canSave}>
          {t('common.action.save')}
        </Button>
      }
      cancelButton={
        <Button onClick={handleClose} color={'secondary'}>
          {t('common.action.cancel')}
        </Button>
      }
    >
      <div className="p-2">
        <Input
          type="text"
          placeholder={t('workspace.folderCreate.namePlaceholder')}
          value={name}
          onChange={handleChange}
          autoFocus={true}
        />
      </div>
    </GeneralModal>
  );
};
